import "./AppHeader.scss";
import styled from "styled-components";
import Logo from "../../images/ovr-logo.png";
import { Spacer } from "../layout/Spacer";
import { Paths } from "../../paths";
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { NavigateFunction, useNavigate } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { Box } from "../styled-system/Box";
import { useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { authSelectors } from "../../store/auth/authSlice";

const HeaderLayout = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 88px;
  padding: 0 104px;
`;

const LogoImage = styled.img`
  height: 56px;
  cursor: pointer;
`;

const DesktopLayout = ({ navigate, isAuthenticated }: { navigate: NavigateFunction; isAuthenticated: boolean }) => {
  return (
    <HeaderLayout className="heading-font">
      <LogoImage src={Logo} alt="Old Vine Registry" onClick={() => navigate("/")} />
      <Spacer />
      <span className="menu-item" onClick={() => navigate("/")}>
        Home
      </span>
      {<a
        className="menu-heading"
        href={`${Paths.advancedSearch}`}
        target="_blank" // Open in a new tab
        rel="noopener noreferrer" // Added for security
        onClick={(e) => {
          e.preventDefault(); // Prevent the default link behavior
          navigate(`${Paths.advancedSearch}?search=false`);
        }}>
        <span className="menu-item u-ml-large">
          Advanced Search
        </span>
      </a>}
      <a
        className="menu-heading"
        href={`${Paths.submit}`}
        target="_blank" // Open in a new tab
        rel="noopener noreferrer" // Added for security
        onClick={(e) => {
          e.preventDefault(); // Prevent the default link behavior
          navigate(`${Paths.submit}`);
        }}>
        <span className="menu-item u-ml-large">
          Submit Vineyard
        </span>
      </a>

      {isAuthenticated && <a
        className="menu-heading"
        href={`${Paths.seal}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.preventDefault();
          navigate(`${Paths.seal}`);
        }}>
        <span className="menu-item u-ml-large">
          OVR Seal
        </span>
      </a>}

      <a
        className="menu-heading"
        href={`${Paths.oldVinesInfo}`}
        target="_blank" // Open in a new tab
        rel="noopener noreferrer" // Added for security
        onClick={(e) => {
          e.preventDefault(); // Prevent the default link behavior
          navigate(`${Paths.oldVinesInfo}`);
        }}>
        <span className="menu-item u-ml-large">
          Why Old Vines?
        </span>
      </a>
      
      <a
        className="menu-heading"
        href={`${Paths.about}`}
        target="_blank" // Open in a new tab
        rel="noopener noreferrer" // Added for security
        onClick={(e) => {
          e.preventDefault(); // Prevent the default link behavior
          navigate(`${Paths.about}`);
        }}>
        <span className="menu-item u-ml-large">
        About
        </span>
      </a>

      <a
        className="menu-heading"
        href={`${Paths.faq}`}
        target="_blank" // Open in a new tab
        rel="noopener noreferrer" // Added for security
        onClick={(e) => {
          e.preventDefault(); // Prevent the default link behavior
          navigate(`${Paths.faq}`);
        }}>
        <span className="menu-item u-ml-large">
        FAQ
        </span>
      </a>
      
      <span className="menu-item u-ml-large">
        <a className="menu-heading"
          href="https://www.oldvines.org/checkout/donate?donatePageId=6462a1da9d9f251340b8f1b2"
          target="_blank"
          rel="noreferrer">
            Donate</a>
      </span>
    </HeaderLayout>
  );
};

const MobileHeaderLayout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 64px;
  padding: 0 24px;
`;

const LogoImageMobile = styled.img`
  height: 36px;
  cursor: pointer;
`;

const MobileLayout = ({ navigate, isAuthenticated }: { navigate: NavigateFunction; isAuthenticated: boolean }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const toggleMenu = () => {
    setMenuVisible((visible) => !visible);
  };

  const nav = (to: string, search?: boolean) => {
    toggleMenu();
    if (search === false) {
      navigate(`${to}?search=false`);
    } else {
      navigate(to);
    }
  };

  return (
    <header>
      <MobileHeaderLayout className="heading-font">
        <LogoImageMobile src={Logo} alt="Old Vine Registry" onClick={() => navigate("/")} />
        <Spacer />
        <GiHamburgerMenu
          size="24px"
          onClick={toggleMenu}
          className="menu-item"
        />
      </MobileHeaderLayout>
      <div className={`menu ${menuVisible ? "menu-expanded" : ""}`}>
        <Box display="flex" flexDirection="column">
          <span className="heading-font menu-item" onClick={() => nav("/")}>
            Home
          </span>
          <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.advancedSearch, false )}
          >
            Advanced Search
          </span>
          <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.submit)}
          >
            Submit Vineyard?
          </span>
          {isAuthenticated && <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.seal)}
          >
            OVR Seal
          </span>}
          <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.oldVinesInfo)}
          >
            Why Old Vines?
          </span>
          <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.about)}
          >
            About
          </span>
          <span
            className="heading-font menu-item u-mt-medium"
            onClick={() => nav(Paths.faq)}
          >
            FAQ
          </span>
          
          <span className="heading-font menu-item u-mt-medium">
          <a className="menu-heading"
              href="https://www.oldvines.org/checkout/donate?donatePageId=6462a1da9d9f251340b8f1b2"
              target="_blank"
              rel="noreferrer">
            Donate</a>
          </span>
        </Box>
      </div>
    </header>
  );
};

export const AppHeader = () => {
  const navigate = useNavigate()
  const bp = useBreakpoint()
  const bearerToken = useAppSelector(authSelectors.selectBearerToken)
  const isAuthenticated = !!bearerToken

  // now bp is one of "mobile" | "tablet" | "desktop"
  return bp === 'mobile' ? <MobileLayout navigate={navigate} isAuthenticated={isAuthenticated} />
    : bp === 'tablet' ? <MobileLayout navigate={navigate} isAuthenticated={isAuthenticated} />
    : <DesktopLayout navigate={navigate} isAuthenticated={isAuthenticated} />
}
